import moment, { Moment } from 'moment';
import * as BBApi from '.';

const rootUrl = BBApi.host + '/events';

export type Event = {
	status: number;
	title: string;
	body: string;
	organizer: string;
	address: string;
	city: string;
	postalCode: number;
	lat: number;
	lng: number;
	price: number;
	bookingLink: string;
	dateStart: string;
	dateEnd: string;
	imageSource: string;
	municipalities: number[];
	dates: [Moment, Moment];
	created?: Date;
};

export const create = async (event: Event & { dates: [Moment, Moment] }) => {
	const [dateStart, dateEnd] = event.dates ?? [null, null];
	if (event.dates) event.dates = [dateStart.utc(), dateEnd.utc()];

	const base64PrefixPattern = /data:image\/\w+;base64/;
	const imageSource = event.imageSource.replace(base64PrefixPattern, '');
	event.imageSource = imageSource;

	const response = await BBApi.post(rootUrl, event);

	if (response.ok === false) {
		throw new Error('Event not created');
	}

	const eventId = await response.text();
	return eventId;
};

export type EventQuery = {
	eventId?: number;
	status: 'all' | 'active' | 'inactive';
	date?: [Moment, Moment];
	amount: number;
};

export type EventQueryResult = {
	events: (Event & { eventId: number })[];
	totalMatches: number;
};

export const search = async (query: EventQuery): Promise<EventQueryResult> => {
	const url = new URL(rootUrl);

	url.searchParams.append('amount', String(query.amount));
	if (query.eventId) url.searchParams.append('eventId', String(query.eventId));
	if (query.status !== 'all') url.searchParams.append('status', query.status);

	const [dateFrom, dateTo] = query.date ?? [null, null];
	if (dateFrom !== null) url.searchParams.append('dateFrom', dateFrom.format('YYYY-MM-DD'));
	if (dateTo !== null) url.searchParams.append('dateTo', dateTo.format('YYYY-MM-DD'));

	const response = await BBApi.get(url.href);

	if (response.ok === false) {
		throw new Error('Search error occurred');
	}

	const searchResult = await response.json();
	return searchResult;
};

export const get = async (eventId: number) => {
	const response = await BBApi.get(rootUrl + `/${eventId}`);

	if (response.ok === false) {
		throw new Error('Event not found');
	}

	const event: Event = await response.json();

	const { dateStart, dateEnd } = event;
	event.dates = [moment(dateStart), moment(dateEnd)];
	return event;
};

export const update = async (eventId: number, event: Event) => {
	if (event.imageSource !== null) {
		const base64PrefixPattern = /data:image\/\w+;base64/;
		const imageSource = event.imageSource.replace(base64PrefixPattern, '');
		event.imageSource = imageSource;
	}

	const response = await BBApi.patch(rootUrl + `/${eventId}`, event);

	if (response.ok === false) {
		throw new Error('Event not updated');
	}
};

export const remove = async (eventId: number) => {
	const response = await BBApi.del(rootUrl + `/${eventId}`);

	if (response.ok === false) {
		throw new Error('Event not deleted');
	}
};

export type OrderData = {
	username: string | null,
	birthYear: number | null,
	municipality: string,
	eventTitle: string,
	plan: string,
	numberOfTickets: number,
	totalPrice: number,
	created: string,
}
export async function latestSubscribers(): Promise<[OrderData]> {
	const url: URL = new URL(rootUrl + '/latest-orders');

	const response: Response = await BBApi.get(url.href);

	if (response.ok === false) {
		const message: string = await response.text();
		throw new Error(message);
	}

	const latestSubscribers = await response.json();
	return latestSubscribers;
}

export type RevenueValuesType = { revenueMTD: number, ordersMTD: number, revenueYTD: number, ordersYTD: number }
export async function revenue(startOfMonth: string, startOfYear: string): Promise<RevenueValuesType> {
	const url: URL = new URL(rootUrl + '/revenue');
	url.searchParams.append('startOfMonth', startOfMonth)
	url.searchParams.append('startOfYear', startOfYear)

	const response: Response = await BBApi.get(url.href);

	if (response.ok === false) {
		const message: string = await response.text();
		throw new Error(message);
	}

	const revenue = await response.json();
	return revenue;
}
